import globalService from "@/services/global.service";
import { LEAVE_FIELD_CODES } from "@/common/leave-field-codes";

/**
 * Mã field phép (leave_field) — nguồn dữ liệu duy nhất đặt tại
 * `@/common/leave-field-codes`. Re-export ở đây để giữ nguyên API công khai
 * hiện có (`import { LEAVE_FIELD_CODES } from "@/services/leave-fund.service"`).
 */
export { LEAVE_FIELD_CODES };

/**
 * Năm go-live của quỹ phép năm — PHẢI khớp với cấu hình backend
 * `annual-leave.first-tracked-year` (xem `workflow_be/src/main/resources/config/application.yml`).
 * Dữ liệu quỹ phép chỉ tồn tại từ năm này trở đi nên các bộ lọc/năm chọn ở FE
 * không được liệt kê năm nhỏ hơn giá trị này.
 */
export const LEAVE_FUND_FIRST_TRACKED_YEAR = 2026;

const BASE_URL = "/api/annual-leave-funds";

/**
 * Dựng query string an toàn từ một object: bỏ qua giá trị `undefined`, `null`
 * và chuỗi rỗng; encode key/value để tránh lỗi ký tự đặc biệt.
 *
 * @param {Object} params - cặp key/value cần đưa vào query string.
 * @returns {string} query string bắt đầu bằng "?" hoặc chuỗi rỗng nếu không có tham số.
 */
function buildQuery(params = {}) {
	const parts = [];
	for (const key of Object.keys(params)) {
		const value = params[key];
		if (value === undefined || value === null || value === "") {
			continue;
		}
		parts.push(
			`${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
		);
	}
	return parts.length ? `?${parts.join("&")}` : "";
}

/**
 * Dịch vụ FE bọc các API quỹ phép năm của backend.
 * Ủy quyền cho `globalService` (đã tự prepend `store.state.config.apiUrl`).
 */
const leaveFundService = {
	LEAVE_FIELD_CODES,

	/**
	 * Danh sách quỹ phép (lọc + phân trang). Dùng getter đọc được header để
	 * caller lấy tổng số bản ghi qua `X-Total-Count`.
	 *
	 * @param {{year?:number|string, organizationId?:string, keyword?:string}} [filter]
	 * @param {{page?:number, size?:number, sort?:string}} [page]
	 * @returns {Promise<import("axios").AxiosResponse>} response đầy đủ (data + headers).
	 */
	list(filter = {}, page = {}) {
		const query = buildQuery({
			year: filter.year,
			organizationId: filter.organizationId,
			keyword: filter.keyword,
			page: page.page,
			size: page.size,
			sort: page.sort,
		});
		return globalService.getData_AsyncWithHeader(`${BASE_URL}${query}`);
	},

	/**
	 * Chi tiết quỹ phép theo cặp (người dùng, năm).
	 *
	 * @param {string} userId
	 * @param {number|string} year
	 */
	detail(userId, year) {
		const query = buildQuery({ userId, year });
		return globalService.getData_Async(`${BASE_URL}/detail${query}`);
	},

	/**
	 * Lịch sử thao tác quỹ phép của một người dùng (mới nhất trước).
	 *
	 * @param {string} userId
	 */
	history(userId) {
		return globalService.getData_Async(
			`${BASE_URL}/history/${encodeURIComponent(userId)}`,
		);
	},

	/**
	 * Lịch sử thao tác quỹ phép của TOÀN BỘ người dùng (không lọc theo userId),
	 * sắp xếp giảm dần theo thời điểm thực hiện, có phân trang server-side.
	 *
	 * <p>Phạm vi dữ liệu trả về phụ thuộc quyền của người đang đăng nhập: Admin hệ thống
	 * thấy toàn bộ; Admin đơn vị chỉ thấy đơn vị mình + các đơn vị con (tất cả các cấp).</p>
	 *
	 * @param {{page?:number, size?:number}} [page]
	 * @returns {Promise<import("axios").AxiosResponse>} response đầy đủ (data + headers),
	 *          dùng header `X-Total-Count` để lấy tổng số bản ghi.
	 */
	historyAll(page = {}) {
		const query = buildQuery({
			page: page.page,
			size: page.size,
		});
		return globalService.getData_AsyncWithHeader(`${BASE_URL}/history${query}`);
	},

	/**
	 * Chỉnh sửa quỹ phép kèm lý do.
	 *
	 * @param {string} id - định danh bản ghi quỹ phép.
	 * @param {{carryoverTotal:number, annualTotal:number, reason:string}} payload
	 * @param {(error:any)=>any} [reject] - handler tùy chọn để caller truy cập lỗi
	 *        backend (JHipster BadRequestAlertException). Mặc định `globalService`
	 *        chỉ log lỗi và trả về `undefined`; truyền handler (ví dụ ném lại lỗi)
	 *        để lấy được thông báo chi tiết như trường hợp giảm dưới số đã dùng.
	 */
	edit(id, payload, reject) {
		return globalService.putData_Async(
			`${BASE_URL}/${encodeURIComponent(id)}`,
			payload,
			undefined,
			reject,
		);
	},

	/**
	 * Tính lại quỹ phép hàng loạt cho năm hiện tại.
	 * Backend nhận `year` (bắt buộc) và `userIds` (tùy chọn) qua @RequestParam.
	 * Nếu `userIds` rỗng/không truyền → backend tính lại cho TẤT CẢ cán bộ của năm.
	 * Chỉ cho phép năm hiện tại (năm cũ đã khóa).
	 *
	 * @param {number|string} year - năm cần tính lại (phải là năm hiện tại).
	 * @param {Array<string>} [userIds] - danh sách cán bộ được chọn; rỗng = tất cả.
	 * @param {(error:any)=>any} [reject] - handler tùy chọn để caller truy cập lỗi
	 *        backend (ví dụ năm đã khóa, năm go-live). Mặc định lỗi bị nuốt và trả về `undefined`.
	 */
	recalculate(year, userIds, reject) {
		let query = buildQuery({ year });
		// userIds là danh sách → nối nhiều tham số cùng tên: userIds=a&userIds=b
		if (Array.isArray(userIds) && userIds.length) {
			const parts = userIds
				.filter((id) => id !== null && id !== undefined && `${id}` !== "")
				.map((id) => `userIds=${encodeURIComponent(id)}`);
			if (parts.length) {
				query = query ? `${query}&${parts.join("&")}` : `?${parts.join("&")}`;
			}
		}
		return globalService.postData_Async(
			`${BASE_URL}/recalculate${query}`,
			{},
			undefined,
			reject,
		);
	},

	/**
	 * Cấp phép thủ công cho một năm chỉ định.
	 * Backend nhận `year` qua @RequestParam.
	 *
	 * @param {number|string} year
	 */
	grant(year) {
		const query = buildQuery({ year });
		return globalService.postData_Async(`${BASE_URL}/grant${query}`, {});
	},

	/**
	 * Cấp phép thủ công cho một người dùng cụ thể (dùng khi danh sách hiển thị
	 * toàn bộ người dùng và một dòng chưa có bản ghi quỹ phép năm đang chọn).
	 * Backend nhận `userId`/`year` qua @RequestParam.
	 *
	 * @param {string} userId
	 * @param {number|string} year
	 * @param {(error:any)=>any} [reject] - handler tùy chọn để caller truy cập lỗi backend.
	 */
	grantUser(userId, year, reject) {
		const query = buildQuery({ userId, year });
		return globalService.postData_Async(
			`${BASE_URL}/grant-user${query}`,
			{},
			undefined,
			reject,
		);
	},

	/**
	 * Thông tin phép hiển thị trên form phiếu nghỉ phép.
	 * Nếu không truyền `userId`, backend lấy theo người đang đăng nhập.
	 *
	 * @param {string} [userId]
	 */
	leaveInfo(userId) {
		const query = buildQuery({ userId });
		return globalService.getData_Async(
			`/api/leave-requests/leave-info${query}`,
		);
	},

	/**
	 * Tính số ngày làm việc trong khoảng `[start, end]` (loại T7/CN và ngày lễ).
	 * Dùng chung endpoint với backend để giá trị xem trước trên form khớp với
	 * giá trị được chốt lúc trình phiếu.
	 *
	 * @param {string} start - ngày bắt đầu nghỉ (định dạng ngày của hệ thống).
	 * @param {string} end - ngày kết thúc nghỉ (định dạng ngày của hệ thống).
	 * @param {(error:any)=>any} [reject] - handler tùy chọn để caller truy cập lỗi
	 *        backend (endpoint trả `BadRequestAlertException` khi tham số không hợp
	 *        lệ hoặc vượt trần khoảng). Mặc định `globalService` chỉ log lỗi và trả
	 *        về `null`; truyền handler để hiển thị toast không chặn phía caller.
	 * @returns {Promise<{workingDays:number, start:string, end:string}>} response
	 *        chứa `workingDays` (số nguyên không âm) cùng `start`/`end` đã nhận.
	 */
	getWorkingDays(start, end, reject) {
		const query = buildQuery({ start, end });
		return globalService.getData_Async(
			`/api/leave-requests/working-days${query}`,
			undefined,
			reject,
		);
	},
};

export default leaveFundService;
